import { inject, Pipe, PipeTransform } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import {
    FormFieldType,
    Locale,
    TranslationsService,
    WdxNumberService,
} from '@wdx/shared/utils';

@Pipe({ name: 'validationErrorMessage' })
export class ValidationErrorMessagePipe implements PipeTransform {
    private translationsService = inject(TranslationsService);
    private wdxNumberService = inject(WdxNumberService);

    transform(
        validationErrors?: null | ValidationErrors,
        formfieldType?: FormFieldType | null,
        locale?: Locale | null,
    ): string {
        if (!validationErrors) {
            return '';
        }

        if (validationErrors['required']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_REQUIRED',
            );
        }

        if (validationErrors['mask']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_MASK',
                { mask: validationErrors['mask'].requiredMask },
            );
        }

        if (validationErrors['min']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_MIN',
                {
                    min:
                        this.formatNumber(
                            validationErrors['min'].min,
                            formfieldType,
                            locale,
                        ) ?? '',
                },
            );
        }

        if (validationErrors['minlength']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_MINLENGTH',
                { minlength: validationErrors['minlength'].requiredLength },
            );
        }

        if (validationErrors['minArrayLength']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_MINARRAYLENGTH',
                { minArrayLength: validationErrors['minArrayLength'] },
            );
        }

        if (validationErrors['max']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_MAX',
                {
                    max:
                        this.formatNumber(
                            validationErrors['max'].max,
                            formfieldType,
                            locale,
                        ) ?? '',
                },
            );
        }

        if (validationErrors['maxlength']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_MAXLENGTH',
                { maxlength: validationErrors['maxlength'].requiredLength },
            );
        }

        if (validationErrors['maxArrayLength']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_MAXARRAYLENGTH',
                { maxArrayLength: validationErrors['maxArrayLength'] },
            );
        }

        if (validationErrors['dateMustBeToday']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_TODAY_ONLY',
            );
        }

        if (validationErrors['dateMustBeBefore']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_BEFORE',
                { dateMustBeBefore: validationErrors['dateMustBeBefore'] },
            );
        }

        if (validationErrors['dateMustBeAfter']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_AFTER',
                { dateMustBeAfter: validationErrors['dateMustBeAfter'] },
            );
        }

        if (validationErrors['datesMustBeValidRange']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_DATERANGE',
            );
        }

        if (validationErrors['mustContainRequiredFields']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_ALLREQUIRED',
            );
        }

        if (validationErrors['isTrue']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_ISTRUE',
            );
        }

        if (validationErrors['regex']) {
            return validationErrors['regex'];
        }

        if (validationErrors['requiredExclusive']) {
            return validationErrors['requiredExclusive'];
        }

        if (validationErrors['mustBeMultipleOf']) {
            return this.translationsService.translateTokenisedString(
                'VALIDATION_MUST_BE_MULTIPLE_OF',
            );
        }

        if (validationErrors['dateMustBeInFuture']) {
            return this.translationsService.translateTokenisedString(
                'FORM_VALIDATION_ERROR_EXPIRY_DATE_FUTURE',
            );
        }

        return this.translationsService.translateTokenisedString(
            'FORM_VALIDATION_ERROR_DEFAULT',
        );
    }

    private formatNumber(
        value: number | null,
        formfieldType?: FormFieldType | null,
        locale?: Locale | null,
    ): string | null {
        switch (formfieldType) {
            case FormFieldType.Integer:
                return this.wdxNumberService.formatNumber(
                    value as number,
                    locale?.code,
                );
            case FormFieldType.Decimal:
            case FormFieldType.Currency:
                // we want to do currency here as we dont want any symbols
                return this.wdxNumberService.formatDecimal(
                    value as number,
                    locale?.code as string,
                );

            default:
                return value?.toString() ?? null;
        }
    }
}
